import { useState, useEffect, useMemo, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { LogoutButton } from '../components/LogoutButton';
import { NotificationsBell } from '../components/NotificationsBell';
import { StatsPanel } from '../components/StatsPanel';
import { MaintenanceOverlay } from '../components/MaintenanceOverlay';
import { useEvent } from '../SSE';
import { motion, AnimatePresence, type Variants } from 'motion/react';
import {
  TosModal,
  CreateServerModal,
  ReferralModal,
  ReferralSuccessModal,
  type DashboardServer,
  type GameVersions,
  type CreateFormData,
} from '../components/dashboard/DashboardModals';
import { type Plan } from '../components/server/ServerModals';
import './Dashboard.css';
import {
  CreditCard, ArrowRightLeft, Plus, Gamepad2, Wallet,
  Server as ServerIcon, ChevronRight, Zap, Power,
  Activity, History, Sparkles,
} from 'lucide-react';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary';

// =========================================================================
// Motion variants — premium, consistent with Home
// =========================================================================

const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1];
const EASE_BACK: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

const heroTitle: Variants = {
  hidden: { opacity: 0, y: 50, scale: 0.94, filter: 'blur(10px)' },
  show:   { opacity: 1, y: 0,  scale: 1,    filter: 'blur(0px)',
    transition: { duration: 1.0, ease: EASE_OUT } },
};

const heroItem: Variants = {
  hidden: { opacity: 0, y: 35, scale: 0.96 },
  show:   { opacity: 1, y: 0,  scale: 1,
    transition: { duration: 0.75, ease: EASE_OUT } },
};

const eyebrow: Variants = {
  hidden: { opacity: 0, y: 18, scale: 0.82 },
  show:   { opacity: 1, y: 0,  scale: 1,
    transition: { duration: 0.55, ease: EASE_BACK } },
};

const sectionReveal: Variants = {
  hidden: { opacity: 0, y: 50 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.85, ease: EASE_OUT } },
};

const cardItem: Variants = {
  hidden: { opacity: 0, y: 45, scale: 0.93 },
  show:   { opacity: 1, y: 0,  scale: 1,
    transition: { duration: 0.65, ease: EASE_OUT } },
};

const stagger: Variants = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};

const tightStagger: Variants = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

// =========================================================================
// Helpers
// =========================================================================

function getVersionsForType(versions: GameVersions | null, game: string, type: string) {
  if (!versions || !versions[game]) return [];
  return versions[game][type] || [];
}

function getTypesForGame(versions: GameVersions | null, game: string): string[] {
  if (!versions || !versions[game]) return [];
  return Object.keys(versions[game]).filter((k) => k !== 'saveData' && k !== 'plans');
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function ServerUptime({ startTime }: { startTime?: string }) {
  const [uptime, setUptime] = useState('00:00:00');

  useEffect(() => {
    if (!startTime) return;
    const update = () => {
      const startStr = startTime.includes('Z') ? startTime : startTime + 'Z';
      const start = new Date(startStr).getTime();
      if (isNaN(start) || start > Date.now()) { setUptime('00:00:00'); return; }
      const diff = Math.floor((Date.now() - start) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setUptime(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  return <span className="db-server-uptime">{uptime}</span>;
}

// =========================================================================
// Main component
// =========================================================================

export function Dashboard() {
  const navigate = useNavigate();
  const {
    state, isLoading: contextLoading,
    fetchDashboardData, fetchServers, updateBalance,
    addServer, removeServer, updateServer,
    updateUser, fetchReferralStatus, setReferralStatus,
    invalidateCache,
  } = useApp();

  // Maintenance gate: bloquea el dashboard entero mientras esté activa.
  // El botón "Crear servidor" se deshabilita más abajo en la misma
  // condición para que el usuario no pueda abrir el modal y enviar un
  // POST que el backend rechazaría con 503.

  // Cross-tab sync via SSE. We patch local state directly from the event
  // payload (no full refetch, no loading spinner) so the dashboard feels
  // snappy. Only `server.updated` falls back to a silent refetch because
  // the event doesn't carry the new plan/version/type values.
  useEvent('server.created', (e) => {
    // Map ServerSummary → Server with sensible defaults. AppContext has
    // extra fields (status, players, plan) that the event doesn't carry.
    addServer({
      id: e.server.id,
      name: e.server.name,
      game: e.server.game,
      type: e.server.type,
      version: e.server.version,
      active: e.server.active,
      // ip is intentionally NOT forwarded from the SSE payload — the
      // backend no longer exposes the VPS IP on ServerSummary.
      // Required so the Dashboard's `s.permissions === 'owner'` filter
      // matches the freshly-patched server. Without it the user just
      // created the server would be invisible until manual refresh.
      permissions: e.server.permissions,
      // Used by the Dashboard's sort to keep newest-first ordering
      // after the SSE-patch inserts a new row.
      created_at: e.server.created_at ?? new Date().toISOString(),
      status: e.server.active ? 'online' : 'offline',
      players: 0,
      maxPlayers: 20,
    });
  });

  useEvent('server.deleted', (e) => {
    removeServer(e.server_id);
  });

  useEvent('server.state_changed', (e) => {
    // Map state machine → active flag. starting/running → true, anything
    // else → false. IP is intentionally NOT in the SSE payload anymore.
    const active = e.state === 'starting' || e.state === 'running';
    updateServer(e.server_id, {
      active,
      status: active ? 'online' : 'offline',
    });
  });

  useEvent('server.updated', () => {
    // Plan/version/type changed: the event doesn't carry the new values,
    // so we silently refetch the servers list (no setIsLoading). The
    // cache is invalidated so the refetch actually hits the API.
    invalidateCache('servers');
    void fetchServers({ force: true });
  });

  useEvent('cash.updated', (e) => {
    invalidateCache('balance');
    updateBalance(e.new_cash);
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<GameVersions | null>(null);
  const [selectedGame, setSelectedGame] = useState('');
  const [availableGames, setAvailableGames] = useState<string[]>([]);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [config, setConfig] = useState<{ maxPlayers: number; maxServersPerUser?: number } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [referralError, setReferralError] = useState('');
  const [showReferralSuccessModal, setShowReferralSuccessModal] = useState(false);
  const [referrerName, setReferrerName] = useState('');
  const [showTosModal, setShowTosModal] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [acceptingTos, setAcceptingTos] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [loadingPlans, setLoadingPlans] = useState(false);

  const [formData, setFormData] = useState<CreateFormData>({
    name: '', game: '', type: '', version: '', maxPlayers: 20,
  });

  // Initial data fetch — always revalidate on mount. We bypass the
  // AppContext cache here because the user might be returning to this
  // page after creating a server in another tab while the Dashboard was
  // not mounted; the SSE event handler below would not have fired in
  // that case, so the cache could be stale up to 5 minutes.
  useEffect(() => {
    if (state.user) setLoading(false);
    if (!state.user) {
      fetchDashboardData().catch((err) => {
        console.error('Error fetching dashboard data:', err);
        setError('Error al cargar datos');
        setLoading(false);
        navigate('/');
      });
      return;
    }
    // User is logged in. Invalidate the servers + balance cache and
    // do a fresh fetch so any change made in another tab while we were
    // unmounted is reflected immediately.
    invalidateCache('servers');
    invalidateCache('balance');
    void fetchDashboardData();
  }, []);

  // Hydrate versions + games
  useEffect(() => {
    if (state.versions) {
      setVersions(state.versions);
      const games = Object.keys(state.versions);
      setAvailableGames(games);
      if (games.length > 0 && !selectedGame) {
        const first = games[0];
        setSelectedGame(first);
        setAvailableTypes(getTypesForGame(state.versions, first));
      }
    }
  }, [state.versions]);

  useEffect(() => {
    if (state.config) setConfig(state.config);
  }, [state.config]);

  // Loading-state resolution
  useEffect(() => {
    if (!contextLoading) {
      if (state.user && state.servers) setLoading(false);
      else if (error || !state.user) {
        setLoading(false);
        if (!state.user) navigate('/');
      }
    }
  }, [contextLoading, state.user, state.servers, error]);

  // Auto-popup: referral
  useEffect(() => {
    if (state.user && !loading && !contextLoading && state.user.tos_accepted) {
      fetchReferralStatus().then((used) => {
        if (used === null) setShowReferralModal(true);
      });
    }
  }, [state.user, loading, contextLoading, fetchReferralStatus]);

  // Auto-popup: TOS
  useEffect(() => {
    if (state.user && !loading && !contextLoading && state.user.tos_accepted === false) {
      setShowTosModal(true);
    }
  }, [state.user, loading, contextLoading]);

  // Initialize create-server form defaults
  useEffect(() => {
    if (availableGames.length > 0 && availableTypes.length > 0 && versions && !formData.game) {
      const firstGame = availableGames[0];
      const firstType = availableTypes[0];
      const gameVersions = getVersionsForType(versions, firstGame, firstType);
      if (gameVersions.length > 0) {
        setFormData((prev) => ({
          ...prev,
          game: firstGame,
          type: firstType,
          version: gameVersions[0].id,
          maxPlayers: config?.maxPlayers || 20,
        }));
      }
    }
  }, [availableGames, availableTypes, versions, config]);

  // Fetch plans when modal opens and game is selected
  useEffect(() => {
    if (showCreateModal && formData.game) {
      fetchPlans(formData.game);
    }
  }, [showCreateModal, formData.game]);

  // Derived data
  const ownedServers = useMemo(
    () =>
      (state.servers || [])
        .filter((s) => s.permissions === 'owner')
        // Newest first. API returns sorted, but SSE-patches can re-shuffle
        // the list when a new server is created in another tab. Sorting
        // here guarantees the visual order matches the user's expectation.
        .sort((a, b) => {
          const at = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bt - at;
        }),
    [state.servers]
  );
  const maxServers = config?.maxServersPerUser || 1;
  const isLimitReached = ownedServers.length >= maxServers;
  const isMaintenanceOn = state.maintenance !== null;
  const activeSessions = useMemo(
    () => (state.servers || []).filter((s) => s.active).length,
    [state.servers]
  );

  const recentActivity = useMemo(() => {
    const payments = (state.payments || []).map((p) => ({
      id: `payment-${p.id}`,
      kind: 'payment' as const,
      description: p.description,
      date: p.date,
      amount: p.amount,
      orderId: p.orderId,
      prefix: '+',
    }));
    const transfers = (state.transfers || []).map((t) => ({
      id: `transfer-${t.id}`,
      kind: 'transfer' as const,
      description:
        t.type === 'sent'
          ? `Enviado a usuario — ${t.concept}`
          : `Recibido de usuario — ${t.concept}`,
      date: t.date,
      amount: t.amount,
      transferType: t.type,
      prefix: t.type === 'sent' ? '-' : '+',
    }));
    return [...payments, ...transfers]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 8);
  }, [state.payments, state.transfers]);

  // Handlers ----------------------------------------------------------------

  const handleTypeChange = (type: string) => {
    const gameVersions = getVersionsForType(versions, selectedGame, type);
    setFormData((prev) => ({ ...prev, type, version: gameVersions[0]?.id || '' }));
  };

  const fetchPlans = async (game: string) => {
    setLoadingPlans(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/plans/${encodeURIComponent(game)}`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok && data.plans) {
        setPlans(data.plans);
        if (data.plans.length > 0) {
          setSelectedPlan(data.plans[0]);
          setFormData((prev) => ({ ...prev, plan_id: data.plans[0].id }));
        } else {
          setSelectedPlan(null);
          setFormData((prev) => ({ ...prev, plan_id: undefined }));
        }
      } else {
        setPlans([]);
        setSelectedPlan(null);
        setFormData((prev) => ({ ...prev, plan_id: undefined }));
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setPlans([]);
      setSelectedPlan(null);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleGameChange = (game: string) => {
    setSelectedGame(game);
    const types = getTypesForGame(versions, game);
    setAvailableTypes(types);
    if (types.length > 0) {
      const firstType = types[0];
      const gameVersions = getVersionsForType(versions, game, firstType);
      if (gameVersions.length > 0) {
        setFormData((prev) => ({ ...prev, game, type: firstType, version: gameVersions[0].id }));
      }
    }
    fetchPlans(game);
  };

  const handlePlanChange = (plan: Plan) => {
    setSelectedPlan(plan);
    setFormData((prev) => ({ ...prev, plan_id: plan.id }));
  };

  const handleCreateServer = async (e: FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        credentials: 'include',
      });
      const result = await res.json();
      if (res.ok) {
        addServer(result.server);
        if (result.newBalance !== undefined) updateBalance(result.newBalance);
        setShowCreateModal(false);
        setFormData({
          name: '',
          game: availableGames[0] || '',
          type: availableTypes[0] || '',
          version: getVersionsForType(versions, selectedGame, availableTypes[0])[0]?.id || '',
          maxPlayers: config?.maxPlayers || 20,
          plan_id: undefined,
        });
        setSelectedPlan(null);
        navigate(`/server/${result.server.id}`);
      } else {
        setCreateError(result.error || 'Error al crear servidor');
      }
    } catch {
      setCreateError('Error de conexión');
    } finally {
      setCreating(false);
    }
  };

  const handleAcceptTos = async () => {
    setAcceptingTos(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/profile/accept-tos`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        if (state.user) updateUser({ ...state.user, tos_accepted: true });
        setShowTosModal(false);
      } else {
        alert('Error al aceptar los términos');
      }
    } catch {
      window.location.reload();
    } finally {
      setAcceptingTos(false);
    }
  };

  const handleReferralSubmit = async (wasInvited: boolean) => {
    setReferralError('');
    if (wasInvited && !referralCodeInput.trim()) return;
    const result = await setReferralStatus(wasInvited, referralCodeInput.trim());
    if (!result.success) {
      setReferralError(result.error || 'Código de referido inválido');
      return;
    }
    if (wasInvited && result.referrerName) {
      setReferrerName(result.referrerName);
      setShowReferralSuccessModal(true);
    }
    setShowReferralModal(false);
  };

  // Early states ------------------------------------------------------------

  if (loading || contextLoading) {
    return (
      <div className="db-page">
        <div className="db-loading">
          <div className="spinner" />
          <p>Cargando…</p>
        </div>
      </div>
    );
  }

  if (error || !state.user) {
    return (
      <div className="db-page">
        <div className="error-card">
          <p>{error || 'Error al cargar datos'}</p>
          <button onClick={() => navigate('/')} className="back-button">Volver</button>
        </div>
      </div>
    );
  }

  // Render ------------------------------------------------------------------

  return (
    <div className="db-page">
      {/* ============================================================
          TOPBAR
          ============================================================ */}
      <header className="sp-topbar">
        <div className="sp-topbar-left">
          <Link to="/dashboard" className="sp-topbar-brand">
            <Zap size={16} />
            <span>{APP_NAME}</span>
          </Link>
          <span className="sp-topbar-divider" aria-hidden="true" />
          <span className="sp-topbar-crumb">Panel</span>
        </div>
        <div className="sp-topbar-right">
          <NotificationsBell />
          <button
            type="button"
            className="sp-topbar-user"
            onClick={() => navigate('/profile')}
            aria-label="Ir al perfil"
          >
            <div className="sp-topbar-avatar">
              {state.user.user_metadata?.avatar_url ? (
                <img src={state.user.user_metadata.avatar_url} alt="" />
              ) : (
                <span>{state.user.email?.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <span className="sp-topbar-user-name">
              {state.user.user_metadata?.full_name || 'Usuario'}
            </span>
          </button>
          <LogoutButton />
        </div>
      </header>

      <motion.main
        className="db-main"
        initial="hidden"
        animate="show"
      >
        {/* ============================================================
            HERO
            ============================================================ */}
        <motion.section
          className="db-hero"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          variants={sectionReveal}
        >
          <div className="db-hero-text">
            <span className="db-hero-greeting">¡Hola de nuevo!</span>
            <h1 className="db-hero-name">
              {state.user.user_metadata?.full_name || 'Bienvenido'}
            </h1>
            <p className="db-hero-sub">
              Gestiona tus servidores de juego, recargas y transferencias desde aquí.
            </p>
          </div>
          <motion.button
            type="button"
            className="btn btn-primary db-hero-cta"
            onClick={() => setShowCreateModal(true)}
            disabled={isLimitReached || isMaintenanceOn}
            title={
              isMaintenanceOn
                ? 'La plataforma está en mantenimiento'
                : isLimitReached
                ? 'Has alcanzado el límite de servidores propios'
                : ''
            }
            whileHover={{ y: -3, boxShadow: '0 12px 32px rgba(147, 51, 234, 0.45)' }}
            whileTap={{ scale: 0.96 }}
            transition={{ duration: 0.2 }}
          >
            <Plus size={16} /> Crear servidor
          </motion.button>
        </motion.section>

        {/* ============================================================
            STATS GRID
            ============================================================ */}
        <motion.div
          className="sp-grid"
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
        >
          {/* Saldo */}
          <motion.div
            className="sp-panel sp-col-4 sp-col-sm-12"
            variants={cardItem}
            whileHover={{ y: -4, borderColor: 'var(--accent)' }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
          >
            <div className="sp-panel-header">
              <Wallet className="sp-panel-icon" />
              <h2 className="sp-panel-title">Saldo</h2>
            </div>
            <div className="sp-panel-body db-balance">
              <div className="db-balance-amount">
                <span className="db-balance-currency">$</span>
                {state.balance.toFixed(2).replace('.', ',')}
              </div>
              <div className="db-balance-actions">
                <motion.button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate('/buy-credits')}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15 }}
                >
                  <CreditCard size={14} /> Recargar
                </motion.button>
                <motion.button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => navigate('/transfer')}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15 }}
                >
                  <ArrowRightLeft size={14} /> Transferir
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Servidores */}
          <motion.div
            className="sp-panel sp-col-4 sp-col-sm-12"
            variants={cardItem}
            whileHover={{ y: -4, borderColor: 'var(--accent)' }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
          >
            <div className="sp-panel-header">
              <ServerIcon className="sp-panel-icon" />
              <h2 className="sp-panel-title">Servidores</h2>
            </div>
            <div className="sp-panel-body db-stat">
              <div className="db-stat-amount">
                {ownedServers.length}<span className="db-stat-of">/ {maxServers}</span>
              </div>
              <div className="db-stat-sub">
                {isLimitReached ? 'Límite alcanzado' : `${maxServers - ownedServers.length} disponibles`}
              </div>
            </div>
          </motion.div>

          {/* Activos */}
          <motion.div
            className="sp-panel sp-col-4 sp-col-sm-12"
            variants={cardItem}
            whileHover={{ y: -4, borderColor: 'var(--accent)' }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
          >
            <div className="sp-panel-header">
              <Power className="sp-panel-icon" />
              <h2 className="sp-panel-title">Sesiones activas</h2>
            </div>
            <div className="sp-panel-body db-stat">
              <div className="db-stat-amount">
                {activeSessions}
              </div>
              <div className="db-stat-sub">
                {activeSessions === 1 ? 'servidor encendido' : 'servidores encendidos'}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* ============================================================
            SERVERS
            ============================================================ */}
        <motion.section
          className="sp-panel db-servers-panel"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
        >
          <div className="sp-panel-header">
            <Gamepad2 className="sp-panel-icon" />
            <h2 className="sp-panel-title">Tus servidores</h2>
            <span className="sp-pill sp-pill--mono">{ownedServers.length} / {maxServers}</span>
            <div className="sp-panel-actions">
              <motion.button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setShowCreateModal(true)}
                disabled={isLimitReached || isMaintenanceOn}
                title={
                  isMaintenanceOn
                    ? 'La plataforma está en mantenimiento'
                    : isLimitReached
                    ? 'Límite alcanzado'
                    : ''
                }
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.15 }}
              >
                <Plus size={14} /> Crear
              </motion.button>
            </div>
          </div>

          <div className="sp-panel-body sp-panel-body--flush">
            {ownedServers.length === 0 ? (
              <motion.div
                className="db-empty"
                variants={sectionReveal}
              >
                <motion.div
                  className="db-empty-icon"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <Gamepad2 size={48} />
                </motion.div>
                <h3>Aún no tienes servidores</h3>
                <p>Crea tu primer servidor de Minecraft, Counter-Strike o Terraria en menos de 2 minutos.</p>
                <motion.button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowCreateModal(true)}
                  disabled={isLimitReached || isMaintenanceOn}
                  whileHover={{ y: -2, boxShadow: '0 12px 32px rgba(147, 51, 234, 0.45)' }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ duration: 0.2 }}
                >
                  <Plus size={14} /> Crear mi primer servidor
                </motion.button>
              </motion.div>
            ) : (
              <motion.div
                className="db-servers-grid"
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-60px' }}
              >
                {ownedServers.map((server) => {
                  const status = server.status;
                  return (
                    <motion.button
                      key={server.id}
                      type="button"
                      className={`db-server-card ${status}`}
                      onClick={() => navigate(`/server/${server.id}`)}
                      variants={cardItem}
                      whileHover={{ y: -4, scale: 1.01, borderColor: 'var(--accent)' }}
                      whileTap={{ scale: 0.99 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className={`db-server-stripe db-server-stripe--${status}`} aria-hidden="true" />
                      <div className="db-server-body">
                        <div className="db-server-head">
                          <span className="db-server-name">{server.name}</span>
                          <ChevronRight size={16} className="db-server-arrow" />
                        </div>
                        <div className="db-server-meta">
                          <span className="db-server-game">{server.game}</span>
                          <span className="db-server-dot" aria-hidden="true" />
                          <span className="db-server-type">{capitalize(server.type)}</span>
                          <span className="db-server-dot" aria-hidden="true" />
                          <span className="db-server-version">v{server.version}</span>
                        </div>
                        <div className="db-server-foot">
                          <span className={`sp-status sp-status--${status === 'online' ? 'online' : status === 'maintenance' ? 'maintenance' : 'offline'}`}>
                            <span className="sp-status-dot" />
                            {status === 'online' ? 'En línea' : status === 'maintenance' ? 'Mantenimiento' : 'Apagado'}
                          </span>
                          {server.active && server.serverStartTime && (
                            <ServerUptime startTime={server.serverStartTime} />
                          )}
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </motion.div>
            )}
          </div>
        </motion.section>

        {/* ============================================================
            ACTIVITY
            ============================================================ */}
        <motion.section
          className="sp-panel db-activity-panel"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
        >
          <div className="sp-panel-header">
            <History className="sp-panel-icon" />
            <h2 className="sp-panel-title">Actividad reciente</h2>
            <span className="sp-pill sp-pill--mono">{recentActivity.length}</span>
          </div>
          <div className="sp-panel-body sp-panel-body--flush">
            {recentActivity.length === 0 ? (
              <motion.div
                className="db-empty"
                variants={sectionReveal}
              >
                <motion.div
                  className="db-empty-icon"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <CreditCard size={40} />
                </motion.div>
                <h3>Sin actividad aún</h3>
                <p>Cuando recargues saldo o recibas transferencias, aparecerán aquí.</p>
              </motion.div>
            ) : (
              <motion.ul
                className="db-activity-list"
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-60px' }}
              >
                {recentActivity.map((item) => (
                  <motion.li
                    key={item.id}
                    className="db-activity-item"
                    variants={cardItem}
                    whileHover={{ x: 4, borderColor: 'var(--border-strong, rgba(255,255,255,0.14))' }}
                    whileTap={{ scale: 0.99 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => {
                      if (item.kind === 'payment') {
                        navigate(`/payment/success?orderId=${item.orderId || item.id.replace('payment-', '')}`);
                      } else if (item.kind === 'transfer') {
                        navigate(`/transfer/success?id=${item.id.replace('transfer-', '')}`);
                      }
                    }}
                  >
                    <div className={`db-activity-icon db-activity-icon--${item.prefix === '+' ? 'in' : 'out'}`}>
                      {item.prefix === '+' ? <Sparkles size={16} /> : <ArrowRightLeft size={16} />}
                    </div>
                    <div className="db-activity-info">
                      <span className="db-activity-desc">{item.description}</span>
                      <span className="db-activity-date">{item.date}</span>
                    </div>
                    <span className={`db-activity-amount db-activity-amount--${item.prefix === '+' ? 'in' : 'out'}`}>
                      {item.prefix}${typeof item.amount === 'number' ? item.amount.toFixed(2) : item.amount}
                    </span>
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </div>
        </motion.section>

        {/* ============================================================
            Live resources (only when at least one server is active)
            ============================================================ */}
        {activeSessions > 0 && state.servers?.find((s) => s.active) && (
          <motion.section
            className="sp-panel db-resources-panel"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            variants={sectionReveal}
          >
            <div className="sp-panel-header">
              <Activity className="sp-panel-icon" />
              <h2 className="sp-panel-title">Recursos en vivo</h2>
              <span className="sp-pill sp-pill--success">
                <motion.span
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                >
                  ●
                </motion.span>{' '}EN VIVO
              </span>
            </div>
            <div className="sp-panel-body sp-panel-body--flush">
              <StatsPanel
                serverId={state.servers!.find((s) => s.active)!.id}
                isActive={true}
                showHeader={false}
              />
            </div>
          </motion.section>
        )}
      </motion.main>

      {/* ============================================================
          MODALS
          ============================================================ */}
      {showTosModal && (
        <TosModal
          appName={APP_NAME}
          accepted={tosAccepted}
          accepting={acceptingTos}
          onAccept={handleAcceptTos}
          onToggle={setTosAccepted}
        />
      )}

      {showCreateModal && (
        <CreateServerModal
          appName={APP_NAME}
          formData={formData}
          availableGames={availableGames}
          availableTypes={availableTypes}
          versions={versions}
          selectedGame={selectedGame}
          maxPlayersDefault={config?.maxPlayers || 20}
          plans={plans}
          loadingPlans={loadingPlans}
          selectedPlan={selectedPlan}
          error={createError}
          creating={creating}
          onChangeName={(v) => setFormData((prev) => ({ ...prev, name: v }))}
          onSelectGame={handleGameChange}
          onSelectType={handleTypeChange}
          onSelectVersion={(v) => setFormData((prev) => ({ ...prev, version: v }))}
          onSelectPlan={handlePlanChange}
          onSubmit={handleCreateServer}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showReferralModal && (
        <ReferralModal
          appName={APP_NAME}
          code={referralCodeInput}
          error={referralError}
          onChangeCode={setReferralCodeInput}
          onSubmit={handleReferralSubmit}
        />
      )}

      {showReferralSuccessModal && (
        <ReferralSuccessModal
          appName={APP_NAME}
          referrerName={referrerName}
          onClose={() => setShowReferralSuccessModal(false)}
        />
      )}

      {/*
        Overlay de mantenimiento. Bloquea el dashboard entero (z-index 9999
        + backdrop con blur) mientras AppContext reporta una ventana
        activa. El backend de /power, /restart y /start-container ya
        rechaza estas acciones con 503, pero el overlay evita que el
        usuario llegue a mandarlas.
      */}
      {state.maintenance && (
        <MaintenanceOverlay
          reason={state.maintenance.reason}
          end={state.maintenance.end}
        />
      )}
    </div>
  );
}
